import React, { useEffect, useMemo } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Mic, MicOff, RecordVoiceOver } from '@mui/icons-material';
import { useVoiceInput } from '../../hooks/useVoiceInput';

type VoiceChatProps = {
  currentText?: string;
  onChange: (text: string) => void;
  onCommit?: () => void;
};

export function VoiceChat({ currentText, onChange, onCommit }: VoiceChatProps) {
  const {
    speechSupported,
    speechListening,
    voiceChatActive,
    toggleSpeech,
    toggleVoiceChat,
    resetSpeechBuffers,
  } = useVoiceInput({
    text: currentText ?? '',
    setText: onChange,
    onCommit,
  });

  useEffect(() => {
    resetSpeechBuffers();
  }, [currentText, resetSpeechBuffers]);

  const speechTooltip = useMemo(() => {
    if (!speechSupported) return 'Diktat nicht verfügbar';
    return speechListening && !voiceChatActive ? 'Diktat stoppen' : 'Diktat starten';
  }, [speechListening, speechSupported, voiceChatActive]);

  const voiceTooltip = useMemo(() => {
    if (!speechSupported) return 'Voice-Chat nicht verfügbar';
    return voiceChatActive ? 'Voice-Chat stoppen' : 'Voice-Chat starten (Auto-Senden nach 3s Stille)';
  }, [speechSupported, voiceChatActive]);

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Tooltip title={speechTooltip}>
        <span>
          <IconButton
            size="small"
            onClick={toggleSpeech}
            disabled={!speechSupported || voiceChatActive}
            aria-label={speechListening && !voiceChatActive ? 'Diktat stoppen' : 'Diktat starten'}
            color={speechListening && !voiceChatActive ? 'error' : 'default'}
          >
            {speechListening && !voiceChatActive ? <MicOff /> : <Mic />}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={voiceTooltip}>
        <span>
          <IconButton
            size="small"
            onClick={toggleVoiceChat}
            disabled={!speechSupported}
            aria-label={voiceChatActive ? 'Voice-Chat stoppen' : 'Voice-Chat starten'}
            color={voiceChatActive ? 'primary' : 'default'}
          >
            <RecordVoiceOver />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}
